import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform, useSpring, useInView } from 'framer-motion';
import { 
  Terminal, Zap, FileText, Globe, Sparkles, Clock, Check,
  Star, ArrowRight, ChevronDown, Plane, Bitcoin, Car,
  Hash, Phone, HelpCircle, CreditCard, Moon, Sun
} from 'lucide-react';
import { Button } from "@/components/ui/button";

// Floating Particle Component
const FloatingParticle = ({ delay, duration, size, color, left, top }) => (
  <motion.div
    className="absolute rounded-full pointer-events-none"
    style={{
      width: size,
      height: size,
      background: color,
      left: `${left}%`,
      top: `${top}%`,
      filter: 'blur(1px)',
    }}
    animate={{
      y: [-20, 20, -20],
      x: [-10, 10, -10],
      opacity: [0.3, 0.8, 0.3],
      scale: [1, 1.2, 1],
    }}
    transition={{
      duration: duration,
      delay: delay,
      repeat: Infinity,
      ease: "easeInOut"
    }}
  />
);

// Parallax Layer Component
const ParallaxLayer = ({ children, speed = 0.5, className = "" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  
  const y = useTransform(scrollYProgress, [0, 1], [0, speed * 200]);
  const smoothY = useSpring(y, { stiffness: 100, damping: 30 });
  
  return (
    <motion.div ref={ref} style={{ y: smoothY }} className={className}>
      {children}
    </motion.div>
  );
};

// Animated Counter
const AnimatedCounter = ({ end, suffix = '', duration = 2 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  
  useEffect(() => {
    if (isInView) {
      let start = 0;
      const increment = end / (duration * 60);
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 1000 / 60);
      return () => clearInterval(timer);
    }
  }, [isInView, end, duration]);
  
  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
};

// Feature Card with 3D Tilt
const FeatureCard = ({ icon: Icon, title, description, delay, gradient, isDark }) => {
  const [rotateX, setRotateX] = useState(0);
  const [rotateY, setRotateY] = useState(0);
  
  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    setRotateX((y - centerY) / 10);
    setRotateY((centerX - x) / 10);
  };
  
  const handleMouseLeave = () => {
    setRotateX(0);
    setRotateY(0);
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, delay }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        transform: `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`,
        transformStyle: 'preserve-3d',
      }}
      className="group relative p-6 md:p-8 rounded-2xl cursor-pointer transition-all duration-300"
    >
      {/* Glassmorphism background */}
      <div className={`absolute inset-0 rounded-2xl ${isDark ? 'bg-white/5' : 'bg-gray-100'} backdrop-blur-xl border ${isDark ? 'border-white/10 group-hover:border-[#ccff00]/50' : 'border-gray-200 group-hover:border-[#ccff00]'} transition-colors`} />
      
      {/* Glow effect */}
      <div className={`absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${gradient}`} style={{ filter: 'blur(40px)' }} />
      
      <div className="relative z-10" style={{ transform: 'translateZ(50px)' }}>
        <div className={`w-16 h-16 rounded-xl ${gradient} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
          <Icon className="w-8 h-8 text-black" />
        </div>
        <h3 className={`text-xl md:text-2xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'} group-hover:text-[#ccff00] transition-colors`}>
          {title}
        </h3>
        <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
          {description}
        </p>
      </div>
    </motion.div>
  );
};

// Category Card
const CategoryCard = ({ icon: Icon, title, emoji, isPremium, isDark }) => (
  <motion.div
    whileHover={{ scale: 1.05, y: -10 }}
    className={`relative flex-shrink-0 w-44 p-6 rounded-xl ${isDark ? 'bg-white/5 border-white/10 hover:border-[#ccff00]/50' : 'bg-gray-100 border-gray-200 hover:border-[#ccff00]'} backdrop-blur-lg border text-center cursor-pointer group transition-all`}
  >
    {isPremium && (
      <div className="absolute -top-2 -right-2 px-2 py-0.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full text-xs font-bold text-white">
        PREMIUM
      </div>
    )}
    <div className="text-4xl mb-3 group-hover:scale-125 transition-transform">{emoji}</div>
    <Icon className={`w-8 h-8 mx-auto mb-2 text-[#ccff00] ${isDark ? 'group-hover:text-white' : 'group-hover:text-gray-900'} transition-colors`} />
    <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{title}</p>
  </motion.div>
);

// Main Landing Page
export default function LandingPageEnhanced({ onGetStarted, theme, toggleTheme }) {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });
  
  const isDark = theme === 'dark';
  
  // Generate particles
  const particles = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    delay: Math.random() * 5,
    duration: 5 + Math.random() * 10,
    size: 2 + Math.random() * 4,
    color: ['#ccff00', '#00ffff', '#ff00ff', '#ffff00'][Math.floor(Math.random() * 4)],
    left: Math.random() * 100,
    top: Math.random() * 100,
  }));

  const features = [
    { icon: Hash, title: "10 Keywords", description: "Add up to 10 keywords for diverse content generation", gradient: "bg-gradient-to-br from-[#ccff00] to-green-400" },
    { icon: Phone, title: "Fancy Numbers", description: "Unicode, emojis, and decorative phone formats", gradient: "bg-gradient-to-br from-blue-400 to-cyan-400" },
    { icon: FileText, title: "PDF Export", description: "Export up to 100 pages with colorful emojis", gradient: "bg-gradient-to-br from-purple-400 to-pink-400" },
    { icon: HelpCircle, title: "Auto FAQ", description: "Auto-generate or add custom FAQs", gradient: "bg-gradient-to-br from-orange-400 to-red-400" },
    { icon: Globe, title: "Multi-Language", description: "Support for EN, ES, FR, DE, PT", gradient: "bg-gradient-to-br from-[#ccff00] to-yellow-400" },
    { icon: CreditCard, title: "Crypto Payment", description: "Pay with BNB, SOL, USDT, ETH", gradient: "bg-gradient-to-br from-teal-400 to-emerald-400" },
  ];

  const categories = [
    { icon: Plane, title: "Airlines", emoji: "✈️", isPremium: false },
    { icon: Bitcoin, title: "Crypto", emoji: "₿", isPremium: true },
    { icon: Car, title: "Car Rental", emoji: "🚗", isPremium: true },
  ];

  return (
    <div ref={containerRef} className={`min-h-screen ${isDark ? 'bg-[#09090b] text-white' : 'bg-white text-gray-900'} overflow-x-hidden transition-colors duration-300`}>
      {/* Progress Bar */}
      <motion.div 
        className="fixed top-0 left-0 right-0 h-1 bg-[#ccff00] z-50 origin-left"
        style={{ scaleX }}
      />
      
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-40 backdrop-blur-xl ${isDark ? 'bg-[#09090b]/80 border-white/10' : 'bg-white/80 border-gray-200'} border-b transition-colors duration-300`}>
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <motion.div 
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.5 }}
              className="w-10 h-10 bg-[#ccff00] flex items-center justify-center rounded-lg"
            >
              <Terminal className="w-6 h-6 text-black" />
            </motion.div>
            <span className="font-bold text-xl tracking-tight">ContentWriter.World</span>
          </div>
          <div className="flex items-center gap-4">
            <button 
              onClick={toggleTheme} 
              className={`p-2 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'} transition-colors`}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <Button 
              onClick={onGetStarted} 
              className="bg-[#ccff00] text-black hover:bg-[#ccff00]/90 font-bold"
            >
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Animated Background */}
        <div className="absolute inset-0">
          {/* Gradient Orbs */}
          <div className={`absolute top-1/4 left-1/4 w-96 h-96 ${isDark ? 'bg-purple-600/30' : 'bg-purple-400/20'} rounded-full filter blur-[100px] animate-pulse`} />
          <div className={`absolute bottom-1/4 right-1/4 w-96 h-96 ${isDark ? 'bg-[#ccff00]/20' : 'bg-[#ccff00]/30'} rounded-full filter blur-[100px] animate-pulse`} style={{ animationDelay: '1s' }} />
          <div className={`absolute top-1/2 left-1/2 w-64 h-64 ${isDark ? 'bg-cyan-500/20' : 'bg-cyan-400/20'} rounded-full filter blur-[80px] animate-pulse`} style={{ animationDelay: '2s' }} />
          
          {/* Floating Particles */}
          {particles.map(p => (
            <FloatingParticle key={p.id} {...p} />
          ))}
          
          {/* Grid */}
          <div className={`absolute inset-0 ${isDark ? 'opacity-20' : 'opacity-10'}`} style={{
            backgroundImage: `linear-gradient(rgba(204,255,0,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(204,255,0,0.1) 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }} />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 text-center px-4 max-w-6xl mx-auto">
          <ParallaxLayer speed={-0.3}>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
            >
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-[#ccff00] font-mono text-sm md:text-base tracking-[0.3em] uppercase mb-6"
              >
                AI-Powered SEO Content Generator
              </motion.p>
              
              <h1 className="text-5xl md:text-7xl lg:text-8xl font-black mb-8 leading-tight">
                <motion.span 
                  initial={{ opacity: 0, x: -100 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5, duration: 0.8 }}
                  className="block"
                >
                  Generate SEO Content
                </motion.span>
                <motion.span 
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7, duration: 0.8 }}
                  className="block text-[#ccff00]"
                >
                  In Seconds
                </motion.span>
              </h1>
              
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.1 }}
                className={`text-lg md:text-xl ${isDark ? 'text-gray-400' : 'text-gray-600'} max-w-3xl mx-auto mb-10 leading-relaxed`}
              >
                Create keyword-rich content with <span className="text-[#ccff00] font-bold">fancy phone numbers</span>, 
                <span className="text-purple-500 font-bold"> colorful emojis</span>, and 
                <span className="text-cyan-500 font-bold"> Unicode characters</span>. 
                Export to PDF instantly.
              </motion.p>

              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.3 }}
                className="flex flex-col sm:flex-row gap-4 justify-center mb-12"
              >
                <motion.button
                  onClick={onGetStarted}
                  whileHover={{ scale: 1.05, boxShadow: '0 0 40px rgba(204,255,0,0.5)' }}
                  whileTap={{ scale: 0.95 }}
                  className="group relative px-10 py-5 bg-[#ccff00] text-black font-bold text-lg rounded-xl overflow-hidden transition-all"
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    Login with Activation Key
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </motion.button>
                <motion.a
                  href="#how-it-works"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`px-10 py-5 border ${isDark ? 'border-white/20 hover:border-[#ccff00] text-white' : 'border-gray-300 hover:border-[#ccff00] text-gray-900'} font-bold text-lg rounded-xl transition-all`}
                >
                  How It Works
                </motion.a>
              </motion.div>

              {/* Stats */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5 }}
                className="flex flex-wrap justify-center gap-8"
              >
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-black text-[#ccff00]"><AnimatedCounter end={100} suffix="+" /></div>
                  <div className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Pages/PDF</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-black text-[#ccff00]"><AnimatedCounter end={10} /></div>
                  <div className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Keywords</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-black text-[#ccff00]"><AnimatedCounter end={5} suffix="+" /></div>
                  <div className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Languages</div>
                </div>
              </motion.div>
            </motion.div>
          </ParallaxLayer>
        </div>

        {/* Scroll Indicator */}
        <motion.div 
          animate={{ y: [0, 15, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <ChevronDown className="w-8 h-8 text-[#ccff00]" />
        </motion.div>
      </section>

      {/* Categories Section */}
      <section className="py-20 px-4 relative">
        <ParallaxLayer speed={0.2}>
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <p className="text-[#ccff00] font-mono text-sm tracking-[0.3em] uppercase mb-4">Categories</p>
              <h2 className="text-4xl md:text-5xl font-bold">
                Choose Your <span className="text-[#ccff00]">Niche</span>
              </h2>
            </motion.div>
            
            <div className="flex flex-wrap justify-center gap-6">
              {categories.map((cat, i) => (
                <motion.div
                  key={cat.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <CategoryCard {...cat} isDark={isDark} />
                </motion.div>
              ))}
            </div>
          </div>
        </ParallaxLayer>
      </section>

      {/* Features Section */}
      <section className={`py-20 px-4 relative ${isDark ? 'bg-[#18181b]/50' : 'bg-gray-50'}`}>
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-[#ccff00] font-mono text-sm tracking-[0.3em] uppercase mb-4">Features</p>
            <h2 className="text-4xl md:text-5xl font-bold">
              Powerful <span className="text-[#ccff00]">Features</span>
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <FeatureCard key={feature.title} {...feature} delay={i * 0.1} isDark={isDark} />
            ))}
          </div>
        </div>
      </section>

      {/* AI Blog Feature Highlight */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-r from-purple-900/20 via-transparent to-[#ccff00]/10' : 'bg-gradient-to-r from-purple-100/50 via-transparent to-[#ccff00]/10'}`} />
        
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className={`${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'} backdrop-blur-xl rounded-3xl p-8 md:p-12 border`}
          >
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#ccff00]/20 rounded-full text-[#ccff00] text-sm font-bold mb-4">
                  <Sparkles className="w-4 h-4" />
                  NEW FEATURE
                </div>
                <h3 className="text-3xl md:text-4xl font-bold mb-4">
                  AI-Powered <span className="text-[#ccff00]">Blog Generator</span>
                </h3>
                <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} mb-6 leading-relaxed`}>
                  Generate complete SEO blog articles with AI. Enter your topic, add hyperlinks, 
                  and get a professional article with URLs mentioned 20+ times. 
                  Download as PDF with clickable links!
                </p>
                <ul className="space-y-3 mb-6">
                  {['AI-generated 800-1200 word articles', 'URLs repeated 20+ times for SEO', 'PDF export with clickable hyperlinks', '5 languages supported'].map((item, i) => (
                    <motion.li 
                      key={i} 
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                      className={`flex items-center gap-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
                    >
                      <Check className="w-5 h-5 text-[#ccff00]" />
                      {item}
                    </motion.li>
                  ))}
                </ul>
                <Button onClick={onGetStarted} className="bg-[#ccff00] text-black hover:bg-[#ccff00]/90 font-bold">
                  Try AI Blog Generator
                </Button>
              </div>
              <div className="flex-1 flex justify-center">
                <motion.div
                  animate={{ y: [-10, 10, -10] }}
                  transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
                  className="relative"
                >
                  <div className="w-64 h-80 bg-gradient-to-br from-purple-500 to-[#ccff00] rounded-2xl p-1">
                    <div className={`w-full h-full ${isDark ? 'bg-[#09090b]' : 'bg-white'} rounded-xl p-4 flex flex-col`}>
                      <div className="flex gap-2 mb-4">
                        <div className="w-3 h-3 rounded-full bg-red-500" />
                        <div className="w-3 h-3 rounded-full bg-yellow-500" />
                        <div className="w-3 h-3 rounded-full bg-green-500" />
                      </div>
                      <div className="space-y-2 flex-1">
                        <div className="h-4 bg-[#ccff00]/30 rounded w-3/4" />
                        <div className={`h-3 ${isDark ? 'bg-white/20' : 'bg-gray-200'} rounded w-full`} />
                        <div className={`h-3 ${isDark ? 'bg-white/20' : 'bg-gray-200'} rounded w-5/6`} />
                        <div className={`h-3 ${isDark ? 'bg-white/20' : 'bg-gray-200'} rounded w-full`} />
                        <div className="h-3 bg-purple-500/30 rounded w-2/3" />
                        <div className={`h-3 ${isDark ? 'bg-white/20' : 'bg-gray-200'} rounded w-full`} />
                        <div className={`h-3 ${isDark ? 'bg-white/20' : 'bg-gray-200'} rounded w-4/5`} />
                        <div className="h-3 bg-[#ccff00]/20 rounded w-3/4" />
                        <div className={`h-3 ${isDark ? 'bg-white/20' : 'bg-gray-200'} rounded w-full`} />
                      </div>
                      <div className="flex gap-2 mt-4">
                        <div className="flex-1 h-8 bg-[#ccff00] rounded flex items-center justify-center">
                          <FileText className="w-4 h-4 text-black" />
                        </div>
                        <div className="flex-1 h-8 bg-purple-500 rounded flex items-center justify-center">
                          <ArrowRight className="w-4 h-4 text-white" />
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">How It Works</h2>
            <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>Get started in 3 simple steps</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {/* Step 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className={`p-6 ${isDark ? 'bg-[#18181b] border-[#3f3f46]' : 'bg-white border-gray-200'} border rounded-xl text-center`}
            >
              <div className="w-12 h-12 bg-[#ccff00] text-black rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">1</div>
              <h4 className="text-lg font-bold mb-2">Get Activation Code</h4>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Get your activation code from <a href="https://quickindex.pro" target="_blank" rel="noopener noreferrer" className="text-[#ccff00] underline">QuickIndex.Pro</a>
              </p>
            </motion.div>

            {/* Step 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className={`p-6 ${isDark ? 'bg-[#18181b] border-[#3f3f46]' : 'bg-white border-gray-200'} border rounded-xl text-center`}
            >
              <div className="w-12 h-12 bg-[#ccff00] text-black rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">2</div>
              <h4 className="text-lg font-bold mb-2">Login & Configure</h4>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Enter keywords, phone numbers, and select your category & language
              </p>
            </motion.div>

            {/* Step 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className={`p-6 ${isDark ? 'bg-[#18181b] border-[#ccff00]' : 'bg-white border-[#ccff00]'} border-2 rounded-xl text-center`}
            >
              <div className="w-12 h-12 bg-[#ccff00] text-black rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">3</div>
              <h4 className="text-lg font-bold mb-2">Generate & Download</h4>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Generate 1-20 unique PDFs instantly. 1 credit = 1 PDF
              </p>
            </motion.div>
          </div>

          {/* QuickIndex.Pro Banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 p-6 bg-gradient-to-r from-[#ccff00]/20 to-[#a3cc00]/20 border border-[#ccff00]/50 rounded-xl text-center max-w-2xl mx-auto"
          >
            <Zap className="w-8 h-8 text-[#ccff00] mx-auto mb-3" />
            <h3 className="text-xl font-bold mb-2">Powered by QuickIndex.Pro</h3>
            <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Get your activation code and credits from QuickIndex.Pro to start generating SEO content
            </p>
            <a 
              href="https://quickindex.pro" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#ccff00] text-black font-bold rounded-lg hover:bg-[#b8e600] transition-all"
            >
              Get Activation Code <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-t from-[#ccff00]/10 to-transparent' : 'bg-gradient-to-t from-[#ccff00]/20 to-transparent'}`} />
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto text-center relative z-10"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to <span className="text-[#ccff00]">Generate</span>?
          </h2>
          <p className={`text-xl ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-10`}>
            Start creating SEO content in seconds. No credit card required for trial.
          </p>
          <motion.button
            onClick={onGetStarted}
            whileHover={{ scale: 1.05, boxShadow: '0 0 50px rgba(204,255,0,0.5)' }}
            whileTap={{ scale: 0.95 }}
            className="px-12 py-6 bg-[#ccff00] text-black font-bold text-xl rounded-xl"
          >
            Start Generating FREE
          </motion.button>
        </motion.div>
      </section>

      {/* Contact Section - Email Only */}
      <section className={`py-12 px-4 ${isDark ? 'bg-[#18181b]/50' : 'bg-gray-50'}`}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-6">Contact Us</h2>
          <motion.a 
            href="mailto:support@contentwriter.world"
            whileHover={{ scale: 1.05 }}
            className={`inline-flex items-center gap-3 px-8 py-4 ${isDark ? 'bg-[#09090b] border-white/10 hover:border-[#ccff00]/50' : 'bg-white border-gray-200 hover:border-[#ccff00]'} border rounded-xl transition-all`}
          >
            <svg className="w-6 h-6 text-[#ccff00]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span className="font-bold">support@contentwriter.world</span>
          </motion.a>
        </div>
      </section>

      {/* Footer */}
      <footer className={`py-8 px-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <p className={isDark ? 'text-gray-500' : 'text-gray-600'}>
            © 2024 ContentWriter.World. All rights reserved.
          </p>
          <a 
            href="mailto:support@contentwriter.world" 
            className={`${isDark ? 'text-gray-400' : 'text-gray-600'} hover:text-[#ccff00] transition-colors`}
          >
            support@contentwriter.world
          </a>
        </div>
      </footer>
    </div>
  );
}
